
import React, { useEffect, useState } from 'react';
import { db } from '../../../../firebase';
import { collection, getDocs, query, orderBy, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { InquiryModel, MemberModel } from '../../../../models';
import { Search, Phone, User, School, GraduationCap, MessageSquare, Trash2, CheckCircle2, Clock, Filter } from 'lucide-react';
import AdminPageHeader from '../../frame/RightPanelHeader';
import Pagination from '../../../../components/ui/Pagination';

interface InquiryRootProps {
    inquiries: InquiryModel[];
    currentAdmin: MemberModel | null;
}

const InquiryRoot: React.FC<InquiryRootProps> = ({ inquiries: initialInquiries, currentAdmin }) => {
    const [inquiries, setInquiries] = useState<InquiryModel[]>(initialInquiries);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState<string>('all');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 10;

    useEffect(() => {
        setInquiries(initialInquiries);
    }, [initialInquiries]);



    const handleStatusChange = async (id: string, newStatus: InquiryModel['status']) => {
        try {
            await updateDoc(doc(db, 'inquiries', id), { status: newStatus });
            setInquiries(inquiries.map(inq => inq.id === id ? { ...inq, status: newStatus } : inq));
        } catch (error) {
            console.error('Error updating status:', error);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('상담 문의 내역을 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'inquiries', id));
            setInquiries(inquiries.filter(inq => inq.id !== id));
        } catch (error) {
            console.error('Error deleting inquiry:', error);
        }
    };

    const filteredInquiries = inquiries.filter(inq => {
        const matchesSearch = inq.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inq.school.toLowerCase().includes(searchTerm.toLowerCase()) ||
            inq.phone.includes(searchTerm);
        const matchesStatus = filterStatus === 'all' || inq.status === filterStatus;
        return matchesSearch && matchesStatus;
    });

    const totalPages = Math.ceil(filteredInquiries.length / ITEMS_PER_PAGE);
    const paginatedInquiries = filteredInquiries.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, filterStatus]);

    const getStatusBadge = (status: InquiryModel['status']) => {
        switch (status) {
            case 'new':
                return <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-rose-50 text-rose-600 text-xs font-black ring-1 ring-rose-100"><Clock size={12} /> 신규 접수</span>;
            case 'contacted':
                return <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-amber-50 text-amber-600 text-xs font-black ring-1 ring-amber-100"><Phone size={12} /> 연락 완료</span>;
            case 'completed':
                return <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 text-emerald-600 text-xs font-black ring-1 ring-emerald-100"><CheckCircle2 size={12} /> 상담 완료</span>;
            default:
                return null;
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden w-full animate-in fade-in duration-500">
            <AdminPageHeader
                title="교육상담 관리"
                icon={MessageSquare}
                subtitle={`랜딩 페이지를 통해 접수된 ${inquiries.length}건의 상담 문의를 확인하고 관리합니다.`}
            />

            <div className="flex-1 overflow-y-auto p-8">
                <div className="max-w-7xl mx-auto space-y-8">
                    <header className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
                            <div className="relative flex-1 group">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors" size={18} />
                                <input
                                    className="w-full sm:w-64 pl-12 pr-4 py-3 rounded-lg bg-white border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all shadow-sm font-medium text-sm"
                                    placeholder="이름, 학교, 연락처 검색..."
                                    value={searchTerm}
                                    onChange={e => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="relative">
                                <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                <select
                                    className="w-full sm:w-44 pl-12 pr-10 py-3 rounded-lg bg-white border border-slate-200 focus:ring-2 focus:ring-indigo-500 outline-none transition-all shadow-sm font-bold text-sm appearance-none cursor-pointer"
                                    value={filterStatus}
                                    onChange={e => setFilterStatus(e.target.value)}
                                >
                                    <option value="all">모든 상태</option>
                                    <option value="new">신규 접수</option>
                                    <option value="contacted">연락 완료</option>
                                    <option value="completed">상담 완료</option>
                                </select>
                            </div>
                        </div>
                    </header>

                    {filteredInquiries.length > 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-xl shadow-slate-200/50">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-slate-50/50 border-b border-slate-100">
                                        <tr className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                            <th className="px-8 py-5">신청자 정보</th>
                                            <th className="px-8 py-5">학교 / 학년</th>
                                            <th className="px-8 py-5">문의내용</th>
                                            <th className="px-8 py-5">상태관리</th>
                                            <th className="px-8 py-5 text-right">관리</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {paginatedInquiries.map((inq) => (
                                            <tr key={inq.id} className="hover:bg-slate-50/50 transition-colors group">
                                                <td className="px-8 py-7">
                                                    <div className="flex items-center gap-4">
                                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-black ${inq.type === 'student' ? 'bg-indigo-50 text-indigo-600' : 'bg-violet-50 text-violet-600'}`}>
                                                            {inq.type === 'student' ? '학' : '부'}
                                                        </div>
                                                        <div className="space-y-1">
                                                            <div className="flex items-center gap-2">
                                                                <span className="font-black text-slate-900">{inq.studentName}</span>
                                                                <span className="text-[10px] font-black text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{inq.type === 'student' ? '학생' : '학부모'}</span>
                                                            </div>
                                                            <div className="flex items-center gap-1.5 text-slate-500 font-bold text-xs">
                                                                <Phone size={12} /> {inq.phone}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-7">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2 text-slate-900 font-bold text-sm">
                                                            <School size={14} className="text-slate-400" /> {inq.school}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-indigo-600 font-black text-[10px] uppercase tracking-wider">
                                                            <GraduationCap size={14} /> {inq.grade}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-7">
                                                    <div className="max-w-xs group-hover:max-w-md transition-all">
                                                        <p className="text-sm font-medium text-slate-600 line-clamp-2 leading-relaxed">
                                                            {inq.content}
                                                        </p>
                                                        <span className="text-[10px] font-black text-slate-300 uppercase block mt-2">
                                                            {new Date(inq.createdAt).toLocaleString()}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-7">
                                                    <div className="flex flex-col gap-2">
                                                        {getStatusBadge(inq.status)}
                                                        <select
                                                            className="text-[10px] font-black border-none bg-slate-100 rounded-lg px-2 py-1 outline-none focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer"
                                                            value={inq.status}
                                                            onChange={(e) => handleStatusChange(inq.id!, e.target.value as InquiryModel['status'])}
                                                        >
                                                            <option value="new">신규 접수</option>
                                                            <option value="contacted">연락 완료</option>
                                                            <option value="completed">상담 완료</option>
                                                        </select>
                                                    </div>
                                                </td>
                                                <td className="px-8 py-7 text-right">
                                                    <button
                                                        onClick={() => handleDelete(inq.id!)}
                                                        className="p-3 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition-all active:scale-90"
                                                    >
                                                        <Trash2 size={20} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="border-t border-slate-100">
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="py-32 bg-white rounded-2xl border border-slate-100 flex flex-col items-center justify-center text-center space-y-6 shadow-sm">
                            <div className="w-24 h-24 bg-slate-50 rounded-xl flex items-center justify-center text-slate-200">
                                <MessageSquare size={48} />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-xl font-black text-slate-800">접수된 문의사항이 없습니다</h3>
                                <p className="text-slate-400 font-medium">검색어나 필터를 조정해 보세요.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default InquiryRoot;

